import { useState } from 'react'
import type { PatientRecord, PatientInsight, PatientRedFlag } from '../lib/types'

interface Props {
  submissionId: number | null
  fetchPatient: (id: number) => Promise<PatientRecord>
  onClose: () => void
}

export function PatientModal({ submissionId, fetchPatient, onClose }: Props) {
  const [patient, setPatient] = useState<PatientRecord | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Fetch when submissionId changes
  if (submissionId !== null && !loading && !patient && !error) {
    setLoading(true)
    fetchPatient(submissionId)
      .then(p => { setPatient(p); setLoading(false) })
      .catch(() => { setError('Failed to load patient record.'); setLoading(false) })
  }

  if (submissionId === null) return null

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setPatient(null); setError(''); onClose()
    }
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="pt-modal-title"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(11,21,32,.6)', zIndex: 500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div style={{
        background: 'var(--card)', borderRadius: 14, boxShadow: '0 8px 32px rgba(1,87,98,.18)',
        width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          padding: '20px 24px 16px', borderBottom: '.5px solid var(--border)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <div id="pt-modal-title" style={{ fontSize: 14, fontWeight: 500, color: 'var(--t1)', lineHeight: 1.3 }}>
              Patient Record
            </div>
            {patient && (
              <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
                #{patient.id} · Tier {patient.tier} · Score {patient.total_score}
              </div>
            )}
          </div>
          <button
            onClick={() => { setPatient(null); setError(''); onClose() }}
            aria-label="Close"
            style={{
              width: 28, height: 28, borderRadius: 6, border: '.5px solid var(--border)',
              background: 'transparent', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              color: 'var(--t3)', fontSize: 16,
            }}
          >×</button>
        </div>
        <div style={{ padding: '20px 24px 24px' }}>
          {loading && <div style={{ textAlign: 'center', padding: '40px 24px', fontSize: 12, color: 'var(--t3)' }}>Loading patient record…</div>}
          {error  && <div style={{ textAlign: 'center', padding: '40px 24px', fontSize: 12, color: 'var(--rose)' }}>{error}</div>}
          {patient && <PatientDetail patient={patient} />}
        </div>
      </div>
    </div>
  )
}

function PatientDetail({ patient }: { patient: PatientRecord }) {
  const copyText = (t: string) => navigator.clipboard.writeText(t)

  return (
    <>
      <div style={{ background: 'var(--teal-lt)', border: '1px solid rgba(1,87,98,.15)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
        {[['NAME', patient.first_name || '—'], ['EMAIL', patient.email]].map(([label, value]) => (
          <div key={label} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: label === 'EMAIL' ? 0 : 6 }}>
            <span style={{ fontSize: 9.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.10em', color: 'var(--teal)', minWidth: 54, flexShrink: 0 }}>{label}</span>
            <span style={{ fontSize: 13, color: 'var(--t1)', fontWeight: 500, wordBreak: 'break-all' }}>{value}</span>
            <button onClick={() => copyText(value)} style={{ fontSize: 10, color: 'var(--teal2)', cursor: 'pointer', marginLeft: 'auto', padding: '1px 6px', borderRadius: 3, border: '1px solid rgba(1,87,98,.2)', background: 'transparent' }}>copy</button>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 16 }}>
        {[
          ['Tier', `${patient.tier} · ${patient.tier_name}`],
          ['Score', String(patient.total_score)],
          ['Age bracket', patient.age_bracket || '—'],
          ['Location', patient.location || '—'],
          ['Fertility goal', patient.fertility_goal || '—'],
          ['Contraception', patient.contraception_status || '—'],
          ['Submitted', new Date(patient.created_at).toLocaleDateString('en-GB')],
          ['Cycle', patient.cycle_classification || '—'],
        ].map(([label, value]) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.09em', color: 'var(--t3)', fontWeight: 500 }}>{label}</span>
            <span style={{ fontSize: 12, color: 'var(--t2)' }}>{value}</span>
          </div>
        ))}
      </div>

      {patient.has_red_flag && patient.red_flag_reasons.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
          {patient.red_flag_reasons.map((r: PatientRedFlag, i) => (
            <div key={i} style={{ background: 'var(--rose-lt)', border: '1px solid rgba(137,68,91,.15)', borderRadius: 6, padding: '8px 10px' }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--rose)', marginBottom: 2 }}>{r.label}</div>
              <div style={{ fontSize: 10, color: 'var(--rose)', opacity: 0.8 }}>{r.urgency}</div>
            </div>
          ))}
        </div>
      ) : patient.has_red_flag ? (
        <div style={{ background: 'var(--rose-lt)', border: '1px solid rgba(137,68,91,.15)', borderRadius: 6, padding: '8px 10px', fontSize: 11, color: 'var(--rose)', marginBottom: 12 }}>
          Red flag flagged
        </div>
      ) : null}

      {patient.top_insights.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {patient.top_insights.map((insight: PatientInsight, i) => (
            <div key={i} style={{ padding: '7px 10px', background: 'var(--alt)', borderRadius: 6, lineHeight: 1.4 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--t1)', marginBottom: 2 }}>
                {insight.icon} {insight.title}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--t2)' }}>{insight.body}</div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
